import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Microphone, 
  PaperPlaneRight,
  Paperclip,
  Camera,
  Smiley,
  X,
  Trash,
  Sticker,
  FloppyDisk
} from '@phosphor-icons/react';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import StickersPanel from './StickersPanel';
import SavedTextsPanel from './SavedTextsPanel';
import PasteImagePreviewModal from './PasteImagePreviewModal';
import { optimizeImageForUpload } from '../utils/imageUpload';

const CHAT_INPUT_MIN_HEIGHT = 40;
const CHAT_INPUT_MAX_HEIGHT = 160;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppInput = ({
  onSendMessage,
  onSendMedia,
  onOpenMediaPicker,
  onTyping,
  placeholder = "Type a message...",
  disabled = false,
  initialMessage = "",
  replyingTo = null,
  onCancelReply = () => {},
  userId = null,
  userRole = 'customer' // customer, handler, admin
}) => {
  const [message, setMessage] = useState(initialMessage);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [showSavedTexts, setShowSavedTexts] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformData, setWaveformData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [pastedImageFile, setPastedImageFile] = useState(null);
  const [pastedImageUrl, setPastedImageUrl] = useState(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimeRef = useRef(0);

  // Handle Clipboard Paste (Win + Shift + S or copied screenshot)
  const handlePaste = useCallback((e) => {
    if (disabled) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type && item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          const url = URL.createObjectURL(file);
          setPastedImageFile(file);
          setPastedImageUrl(url);
          setShowPasteModal(true);
          break;
        }
      }
    }
  }, [disabled]);

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Only capture paste if activeElement is not a different input/textarea
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') && activeEl !== inputRef.current) {
        return;
      }
      handlePaste(e);
    };
    window.addEventListener('paste', handleGlobalPaste);
    return () => {
      window.removeEventListener('paste', handleGlobalPaste);
    };
  }, [handlePaste]);

  const handleSendPastedImage = async ({ file, caption }) => {
    if (!file || !onSendMedia) return;
    try {
      const uploadFile = await optimizeImageForUpload(file);
      const formData = new FormData();
      formData.append('file', uploadFile, uploadFile.name || 'screenshot.webp');
      formData.append('media_type', 'image');

      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedFile = response.data?.file || response.data;
      if (!uploadedFile?.url) {
        throw new Error('Upload completed but no media URL was returned');
      }

      onSendMedia({
        type: 'image',
        url: uploadedFile?.url,
        caption: caption || ''
      });

      toast.success('Screenshot sent');
    } catch (err) {
      console.error('Failed to upload pasted image:', err);
      const reason = err.response?.data?.detail || err.response?.data?.error || err.message;
      toast.error(reason ? `Screenshot upload failed: ${reason}` : 'Failed to send screenshot');
      throw err;
    }
  };

  const handleClosePasteModal = () => {
    if (pastedImageUrl) {
      URL.revokeObjectURL(pastedImageUrl);
    }
    setPastedImageFile(null);
    setPastedImageUrl(null);
    setShowPasteModal(false);
  };

  // Update message when initialMessage changes
  useEffect(() => {
    if (initialMessage && !message) {
      setMessage(initialMessage);
    }
  }, [initialMessage, message]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) {
        try { audioContextRef.current.close(); } catch(e) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle emoji selection
  const onEmojiClick = (emojiObject) => {
    setMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  // Send text message
  const handleSend = () => {
    if (message.trim() && onSendMessage) {
      const currentInput = inputRef.current;
      const textToSend = message.trim();
      
      // Reset height BEFORE clearing message (prevents layout shift)
      if (currentInput) {
        currentInput.style.height = '40px';
      }
      
      // Clear message state
      setMessage('');
      setShowEmojiPicker(false);
      
      // Send the message
      onSendMessage(textToSend);
      
      // Keep focus without losing keyboard (prevents bounce)
      if (currentInput) {
        // Use preventScroll to avoid any viewport shifts
        currentInput.focus({ preventScroll: true });
      }
    }
  };

  // Desktop: Enter sends; Shift+Enter inserts a newline.
  // Mobile keeps Enter as a newline, matching the existing mobile behavior.
  const handleKeyPress = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) return;

    const isMobile = window.matchMedia?.('(max-width: 767px)').matches;
    if (isMobile) return;

    event.preventDefault();
    handleSend();
  };

  const lastTypingNotifyRef = useRef(0);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    const nextMessage = e.target.value;
    setMessage(nextMessage);
    e.target.style.height = `${CHAT_INPUT_MIN_HEIGHT}px`;
    // Keep an empty composer compact. Browsers can retain the previous
    // scrollHeight after a multiline draft is cleared, which makes the
    // customer composer render as a large blank panel.
    if (nextMessage) {
      e.target.style.height = `${Math.min(e.target.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
    }
    if (onTyping) {
      const now = Date.now();
      if (now - lastTypingNotifyRef.current > 1500) {
        lastTypingNotifyRef.current = now;
        onTyping(true);
      }
    }
  };

  // Resize when text is inserted programmatically, for example from the emoji
  // picker or a saved-text shortcut.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = `${CHAT_INPUT_MIN_HEIGHT}px`;
    if (message) {
      input.style.height = `${Math.min(input.scrollHeight, CHAT_INPUT_MAX_HEIGHT)}px`;
    }
  }, [message]);

  // Handle sticker selection
  const handleStickerSelect = (sticker) => {
    if (onSendMessage) {
      // If sticker is a URL (custom sticker), send as image
      if (sticker.startsWith('http') || sticker.startsWith('/')) {
        onSendMedia && onSendMedia({
          type: 'sticker',
          url: sticker
        });
      } else {
        // Emoji sticker - send as text
        onSendMessage(sticker);
      }
    }
    setShowStickers(false);
  };

  // Handle saved text selection
  const handleSavedTextSelect = (text) => {
    if (onSendMessage) {
      onSendMessage(text);
    }
    setShowSavedTexts(false);
  };

  // Update waveform visualization
  const updateWaveform = useCallback(() => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Sample 30 bars for waveform
    const samples = 30;
    const blockSize = Math.floor(dataArray.length / samples);
    const bars = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += dataArray[i * blockSize + j];
      }
      const average = sum / blockSize;
      bars.push(Math.max(4, (average / 255) * 40));
    }
    
    setWaveformData(bars);
    animationRef.current = requestAnimationFrame(updateWaveform);
  }, []);

  // Start voice recording with waveform
  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Voice recording not supported in this browser');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Setup audio context for waveform
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Setup MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        
        // Auto-upload and send (WhatsApp instant-send behavior)
        if (onSendMedia && blob.size > 0) {
          setIsUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', blob, 'voice.webm');
            formData.append('media_type', 'voice');
            const response = await axios.post(`${API}/upload/media`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedFile = response.data?.file || response.data;
            if (!uploadedFile?.url) {
              throw new Error('Upload completed but no media URL was returned');
            }
            onSendMedia({
              type: 'voice',
              url: uploadedFile?.url,
              filename: uploadedFile?.filename,
              duration: recordingTimeRef.current
            });
          } catch (error) {
            console.error('Failed to upload voice:', error);
            toast.error('Voice message send failed');
          } finally {
            setIsUploading(false);
          }
        }
        
        // Reset state
        setRecordingTime(0);
        recordingTimeRef.current = 0;
        setWaveformData([]);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          recordingTimeRef.current = newTime;
          return newTime;
        });
      }, 1000);
      
      // Start waveform animation
      updateWaveform();
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      
      let errorMessage = 'Microphone access denied.\n\n';
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow microphone permission in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      toast.error(errorMessage);
    }
  };

  // Stop recording (auto-sends via onstop callback)
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    // Prevent auto-send by clearing chunks before stopping
    audioChunksRef.current = [];
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setRecordingTime(0);
    recordingTimeRef.current = 0;
    setWaveformData([]);
  };

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Voice Recording UI - Only shows during active recording
  if (isRecording) {
    return (
      <div className="bg-wa-panel border-t p-3">
        {/* Reply Preview in recording mode */}
        {replyingTo && (
          <div className="bg-wa-hover p-2 mb-2 border-l-4 border-green-600 flex justify-between items-start rounded text-sm">
            <div className="flex-1 truncate">
              <span className="text-green-600 font-semibold text-xs">Replying to {replyingTo.sender_name}</span>
              <p className="text-wa-text-secondary truncate">{replyingTo.text || 'Photo'}</p>
            </div>
            <button onClick={onCancelReply} className="text-wa-text-secondary ml-2"><X size={16} /></button>
          </div>
        )}
        
        <div className="flex items-center gap-3 bg-wa-hover rounded-full px-4 py-2">
          {/* Cancel Button */}
          <button
            onClick={cancelRecording}
            className="p-2 text-red-500 hover:bg-wa-hover rounded-full"
            data-testid="cancel-recording-btn"
          >
            <Trash size={24} weight="bold" />
          </button>

          {/* Live Waveform */}
          <div className="flex-1 flex items-center gap-2">
            <div className="flex items-center gap-0.5 h-10 flex-1">
              {waveformData.map((height, i) => (
                <div
                  key={i}
                  className="bg-green-500 rounded-full w-1"
                  style={{ height: `${height}px`, transition: 'height 0.1s ease' }}
                />
              ))}
            </div>
            {/* Recording indicator */}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-mono text-wa-text-secondary">{formatTime(recordingTime)}</span>
            </div>
          </div>

          {/* Stop & Send Button (instant send on stop) */}
          <button
            onClick={stopRecording}
            className="p-3 bg-green-500 text-white rounded-full"
            data-testid="stop-send-recording-btn"
          >
            <PaperPlaneRight size={24} weight="fill" />
          </button>
        </div>
        
        {/* Uploading indicator */}
        {isUploading && (
          <div className="flex items-center justify-center gap-2 mt-2 text-sm text-wa-text-secondary">
            <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            Sending...
          </div>
        )}
      </div>
    );
  }

  // Normal Input UI
  return (
    <div className="bg-wa-panel-header px-2 py-[5px] relative" style={{ paddingBottom: 'max(5px, env(safe-area-inset-bottom))' }}>
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="absolute bottom-16 left-2 z-50">
          <EmojiPicker onEmojiClick={onEmojiClick} height={350} width={300} />
        </div>
      )}

      {/* Stickers Panel */}
      {showStickers && (
        <StickersPanel
          userId={userId}
          userRole={userRole}
          onStickerSelect={handleStickerSelect}
          onClose={() => setShowStickers(false)}
        />
      )}

      {/* Saved Texts Panel (Handler/Admin only) */}
      {showSavedTexts && (userRole === 'handler' || userRole === 'admin') && (
        <SavedTextsPanel
          userId={userId}
          userRole={userRole}
          onTextSelect={handleSavedTextSelect}
          onClose={() => setShowSavedTexts(false)}
        />
      )}

      {/* Reply Preview */}
      {replyingTo && (
        <div className="bg-wa-hover p-2 mb-2 border-l-4 border-green-600 flex justify-between items-start rounded">
          <div className="flex-1">
            <div className="text-xs text-green-600 font-semibold">
              Replying to {replyingTo.sender_name}
            </div>
            <div className="text-sm text-wa-text-primary truncate">
              {replyingTo.text || 'Photo'}
            </div>
          </div>
          <button
            onClick={onCancelReply}
            className="text-wa-text-secondary hover:text-wa-text-primary ml-2"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2" style={{maxWidth: '100vw', overflow: 'hidden'}}>
        {/* WhatsApp-style input container */}
        {/* WhatsApp's composer is a white pill sitting on the grey bar. */}
        <div className="flex-1 flex items-center bg-wa-panel rounded-full px-1 min-h-[44px] min-w-0">
          {/* Emoji Button */}
          <button
            onClick={() => {
              if (disabled) return;
              setShowEmojiPicker(!showEmojiPicker);
              setShowStickers(false);
              setShowSavedTexts(false);
            }}
            disabled={disabled}
            className={`p-2 text-wa-icon hover:text-[#00a884] flex-shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
            data-testid="emoji-btn"
          >
            <Smiley size={24} />
          </button>

          {/* Text Input */}
          <textarea
            ref={inputRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            style={{ maxHeight: `${CHAT_INPUT_MAX_HEIGHT}px`, minHeight: `${CHAT_INPUT_MIN_HEIGHT}px`, resize: 'none' }}
            className="flex-1 bg-transparent py-2.5 px-1 focus:outline-none text-[15px] min-w-0 overflow-y-auto text-wa-text-primary placeholder-[#8696a0] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="chat-input"
          />

          {/* Attachment & Camera - hide when typing */}
          {!message.trim() && (
            <>
              <button
                onClick={() => {
                  if (disabled) return;
                  setShowEmojiPicker(false);
                  setShowStickers(false);
                  setShowSavedTexts(false);
                  if (onOpenMediaPicker) onOpenMediaPicker();
                }}
                disabled={disabled}
                className={`p-2 text-wa-icon hover:text-[#00a884] flex-shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                data-testid="media-picker-btn"
              >
                <Paperclip size={22} />
              </button>
              <button
                className={`p-2 text-wa-icon hover:text-[#00a884] flex-shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                data-testid="camera-btn"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  // Open the media picker (attachment dialog) instead of direct upload
                  if (onOpenMediaPicker) onOpenMediaPicker();
                }}
              >
                <Camera size={22} />
              </button>
            </>
          )}
        </div>

        {/* Send or Voice Button - ALWAYS visible, never hidden */}
        {message.trim() ? (
          <button
            onClick={handleSend}
            disabled={disabled}
            className={`w-[48px] h-[48px] bg-[#00a884] text-white rounded-full hover:bg-[#008f72] flex-shrink-0 flex items-center justify-center ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
            data-testid="send-message-btn"
          >
            <PaperPlaneRight size={22} weight="fill" />
          </button>
        ) : (
          <button
            onClick={startRecording}
            disabled={disabled}
            className={`w-[48px] h-[48px] bg-[#00a884] text-white rounded-full hover:bg-[#008f72] flex-shrink-0 flex items-center justify-center ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
            data-testid="start-recording-btn"
          >
            <Microphone size={22} weight="fill" />
          </button>
        )}
      </div>

      {/* Paste Image Preview Modal */}
      {showPasteModal && (
        <PasteImagePreviewModal
          isOpen={showPasteModal}
          imageFile={pastedImageFile}
          imageUrl={pastedImageUrl}
          onClose={handleClosePasteModal}
          onSend={handleSendPastedImage}
        />
      )}

      {/* Click outside to close emoji picker */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowEmojiPicker(false)}
        />
      )}
    </div>
  );
};

export default WhatsAppInput;
